import { useState, useRef, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { ShoppingCart, Youtube, LogIn, User } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { LOGO_URL, YOUTUBE_URL } from "../lib/brand";

export default function Navbar() {
  const { count, setOpen } = useCart();
  const { user, login, logout } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <header
      className="sticky top-0 z-40 bg-[#FFFDF0] border-b-4 border-black"
      data-testid="site-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-4">
        <Link
          to="/"
          className="flex items-center gap-3"
          data-testid="nav-logo"
        >
          <img
            src={LOGO_URL}
            alt="Anthony's Playworld"
            className="pw-logo-img h-12 sm:h-14 w-auto"
          />
          <span className="hidden sm:inline font-display text-xl text-[#FF2E93] uppercase leading-none">
            Merch&nbsp;Store
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-2">
          <NavItem to="/" label="Home" testid="nav-home" />
          <NavItem to="/shop" label="Shop" testid="nav-shop" />
          <NavItem to="/about" label="About" testid="nav-about" />
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={YOUTUBE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="hidden sm:inline-flex pw-btn pw-btn-sm pw-btn-blue"
            data-testid="nav-youtube"
          >
            <Youtube size={18} strokeWidth={3} className="mr-1" /> YouTube
          </a>

          {user ? (
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setMenuOpen((v) => !v)}
                className="inline-flex items-center gap-2 border-3 border-black rounded-full bg-white px-2 py-1 shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] hover:bg-[#FFD500] transition-colors"
                style={{ borderWidth: 3 }}
                data-testid="nav-user-button"
                aria-label="Open account menu"
              >
                {user.picture ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    className="w-7 h-7 rounded-full border-2 border-black"
                  />
                ) : (
                  <User size={18} strokeWidth={3} />
                )}
                <span className="hidden sm:inline font-bold text-sm pr-1">
                  {user.name?.split(" ")[0] || "Account"}
                </span>
              </button>

              {menuOpen && (
                <div
                  className="absolute right-0 mt-2 w-56 bg-white border-4 border-black rounded-xl shadow-[6px_6px_0px_0px_rgba(10,10,10,1)] overflow-hidden"
                  data-testid="user-menu"
                >
                  <div className="p-3 border-b-3 border-black" style={{ borderBottomWidth: 3 }}>
                    <p className="font-bold truncate">{user.name}</p>
                    <p className="text-xs opacity-70 truncate">{user.email}</p>
                  </div>
                  <Link
                    to="/account"
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 font-bold hover:bg-[#FFD500]"
                    data-testid="menu-account"
                  >
                    My Account
                  </Link>
                  <Link
                    to="/orders"
                    onClick={() => setMenuOpen(false)}
                    className="block px-4 py-2 font-bold hover:bg-[#00E5FF]"
                    data-testid="menu-orders"
                  >
                    My Orders
                  </Link>
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      logout();
                    }}
                    className="block w-full text-left px-4 py-2 font-bold hover:bg-[#FF2E93] hover:text-white"
                    data-testid="menu-logout"
                  >
                    Sign Out
                  </button>
                </div>
              )}
            </div>
          ) : (
            <button
              onClick={login}
              className="pw-btn pw-btn-sm pw-btn-ghost"
              data-testid="nav-login-button"
            >
              <LogIn size={16} strokeWidth={3} className="mr-1" />
              <span className="hidden sm:inline">Sign in</span>
            </button>
          )}

          <button
            className="pw-btn pw-btn-pink pw-btn-sm relative"
            onClick={() => setOpen(true)}
            data-testid="nav-cart-button"
            aria-label="Open cart"
          >
            <ShoppingCart size={18} strokeWidth={3} className="mr-1" />
            Cart
            {count > 0 && (
              <span
                className="ml-2 bg-white text-black border-2 border-black rounded-full min-w-[24px] h-6 px-1.5 inline-flex items-center justify-center text-xs font-bold"
                data-testid="cart-count-badge"
              >
                {count}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}

function NavItem({ to, label, testid }) {
  return (
    <NavLink
      to={to}
      end={to === "/"}
      data-testid={testid}
      className={({ isActive }) =>
        `font-bold uppercase tracking-wide px-4 py-2 rounded-full ${
          isActive
            ? "bg-[#0A0A0A] text-white"
            : "bg-transparent text-black hover:bg-[#FFD500]"
        }`
      }
      style={{ border: "3px solid #0A0A0A" }}
    >
      {label}
    </NavLink>
  );
}
